import React, { useEffect, useState } from 'react';
import { X, MapPin, Plus, Trash2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

let nextDefaultNumber = (courts) => {
  let n = courts.length + 1;
  const labels = new Set(courts.map((c) => c.label));
  while (labels.has(`Court ${n}`)) n += 1;
  return n;
};

/**
 * Configures the physical courts/fields this tournament is played on —
 * organizer-named, not auto-numbered by us (a "Court 1" default is just a
 * starting point to rename): whatever they'd actually call it to players
 * ("Court A", "Field 7", ...) is used verbatim as both the id and the
 * display label, since this is the same value that'll eventually persist to
 * a DB. Once 1+ courts exist, matches start getting auto-assigned across
 * them round-by-round (see scheduleRows.js) — this modal only manages the
 * list itself; per-match/per-round court overrides live in the gear-icon
 * settings modal instead (see MatchSettingsModal.jsx).
 */
const CourtsSettingsModal = ({ isOpen, onClose, courts, onCourtsChange }) => {
  const { theme } = useTheme();
  const [draft, setDraft] = useState(courts);

  // The modal stays mounted between opens (GeneratedScheduleStep just toggles
  // `isOpen`), so without this, reopening after a previous close-without-
  // applying would still show that stale, discarded draft instead of the
  // real current `courts`.
  useEffect(() => {
    if (isOpen) setDraft(courts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const addCourt = () => {
    const label = `Court ${nextDefaultNumber(draft)}`;
    setDraft([...draft, { id: label, label }]);
  };

  const renameCourt = (index, label) => {
    setDraft(draft.map((c, i) => (i === index ? { id: label, label } : c)));
  };

  const removeCourt = (index) => {
    setDraft(draft.filter((_, i) => i !== index));
  };

  const hasDuplicates = new Set(draft.map((c) => c.label.trim())).size !== draft.length;
  const hasEmpty = draft.some((c) => !c.label.trim());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border ${theme.border} ${theme.card} ${theme.shadow}`}>
        <div className={`flex shrink-0 items-center justify-between border-b ${theme.border} px-6 py-4`}>
          <div className={`flex items-center gap-2 text-base font-bold ${theme.text}`}>
            <MapPin size={18} />
            Court / Field Assignment
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${theme.buttonGhost}`}
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <p className={`mb-4 text-sm ${theme.textSecondary}`}>
            Add every physical court or field this tournament will be played on, and name each one whatever you'd
            actually call it to players — "Court 1", "Court A", "Field 7". Once you add any, matches are automatically
            spread across them round by round; you can still reassign any specific match or round afterward.
          </p>

          <div className="space-y-2">
            {draft.map((court, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={court.label}
                  onChange={(e) => renameCourt(index, e.target.value)}
                  placeholder="Court name"
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm ${theme.input}`}
                />
                <button
                  type="button"
                  onClick={() => removeCourt(index)}
                  aria-label={`Remove ${court.label}`}
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors ${theme.buttonGhost}`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            {draft.length === 0 && (
              <p className={`text-sm italic ${theme.textMuted}`}>No courts/fields added yet — matches show no court until you add at least one.</p>
            )}
          </div>

          <button
            type="button"
            onClick={addCourt}
            className={`mt-3 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${theme.border} ${theme.buttonGhost}`}
          >
            <Plus size={15} />
            Add {draft.length === 0 ? 'a' : 'another'} court/field
          </button>

          {hasDuplicates && <p className="mt-3 text-xs font-medium text-red-500">Two courts can't share the same name.</p>}
          {hasEmpty && <p className="mt-3 text-xs font-medium text-red-500">Every court needs a name.</p>}

          <button
            type="button"
            disabled={hasDuplicates || hasEmpty}
            onClick={() => {
              onCourtsChange(draft);
              onClose();
            }}
            className={`mt-5 w-full rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${theme.buttonPrimary} hover:opacity-90 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40`}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourtsSettingsModal;
